import type {
  AdminDetails,
  Agreement,
  GeoLocation,
  Partnership,
  Product,
  ProductAdditionalInfo,
  ProductExtensions,
  Variant,
} from 'app/src/services/welfareportal/companyagreement-api'
import { z } from 'zod'

const dateTimeString = z.string()
const int32 = z.number().int()
const int64 = z.number().int()

export const GeoLocationSchema: z.ZodType<GeoLocation> = z.object({
  latitude: z.number(),
  longitude: z.number(),
})

export const AdminDetailsSchema: z.ZodType<AdminDetails> = z.object({
  createdBy: z.string(),
  createdOn: dateTimeString,
  lastModifiedBy: z.string(),
  lastModifiedOn: dateTimeString,
})

export const PartnershipSchema: z.ZodType<Partnership> = z.object({
  description: z.string(),
  link: z.string(),
})

export const AgreementSchema: z.ZodType<Agreement> = z.object({
  description: z.string(),
  link: z.string(),
})

export const ProductAdditionalInfoSchema: z.ZodType<ProductAdditionalInfo> = z.object({
  partnership: PartnershipSchema.optional(),
  agreement: AgreementSchema.optional(),
  informativeMessage: z.string().optional(),
})

const PriceDetailsSchema = z.object({
  facePrice: z.number(),
  finalGrossPrice: z.number(),
  taxPercentage: z.number(),
  taxClass: z.string().optional(),
  discountType: z.enum(['percentage', 'fixed']).optional(),
  discountValue: z.number().optional(),
  billingData: z.string().optional(),
})

export const VariantSchema: z.ZodType<Variant> = z.object({
  variantId: z.string(),
  externalId: z.string(),
  variantName: z.string(),

  priceDetails: PriceDetailsSchema.optional(),

  active: z.boolean(),

  signature: z.object({
    signature: z.string(),
    validUntil: int64,
  }),

  additionalInfo: z.record(z.string(), z.any()).optional(),

  /* VariantExtensions */
  inStock: z.boolean(),
  mainImage: z.string().optional(),

  targetPerson: z.enum([
    'employee',
    'familyMember',
    'all',
    'familyMemberDependent',
    'dependentChildrenAndSpouse',
    'employeeAndFamilyMemberDependent',
    'allAndAdultWithPrivacy',
  ]),

  targetPersonMultiple: z.boolean(),
  targetPersonDisclaimer: z.string().optional(),

  personalData: z.array(z.string()).optional(),
  personalDataDisclaimer: z.string().optional(),

  informativeMetadata: z.record(z.string(), z.string()).optional(),

  adminDetails: AdminDetailsSchema.optional(),
  weight: int32.optional(),
})

export const ProductExtensionsSchema: z.ZodType<ProductExtensions> = z.object({
  categoryIds: z.array(z.string()),

  description: z.string(),
  details: z.string().optional(),

  mainImage: z.string().optional(),
  images: z.array(z.string()).optional(),

  status: z.enum(['draft', 'inserted', 'confirmed', 'deleted']),

  geoLocation: GeoLocationSchema.optional(),
  geoAddress: z.string().optional(),

  weight: int32.optional(),

  multiPurchase: z.boolean(),

  variants: z.array(VariantSchema),

  adminDetails: AdminDetailsSchema.optional(),

  isFavourite: z.boolean(),

  additionalInfo: ProductAdditionalInfoSchema.optional(),
})

/**
 * Product schema, including both the base properties and the extensions.
 */
export const ProductSchema: z.ZodType<Product> = z
  .object({
    productId: z.string(),
    externalId: z.string(),

    typology: z.enum(['moneyTransfer', 'refund', 'giftCard', 'goodAndService', 'pass', 'voucher', 'discount']),

    requiredAccessLevel: z.enum(['public', 'welfare']),

    merchantType: z.enum(['epipoli', 'amilon', 'pws', 'tantosvago', 'poste', 'entheos', 'osa', 'postewarehouse', 'lastminute']),

    serviceCategory: z.enum(['welfareArt100', 'welfareArt51', 'none']),

    productName: z.string(),

    signature: z.object({
      signature: z.string(),
      validUntil: int64,
    }),

    privacyList: z
      .array(
        z.object({
          privacyId: z.string(),
          mandatory: z.boolean(),
        }),
      )
      .optional(),
  })
  .and(ProductExtensionsSchema)
